/**
 * CreateInvoiceModal — „Neue Rechnung" inkl. Universal-Kundensuche, Inline-
 * Kundenanlage, Garage-Fahrzeug-Übernahme, Schnell-/Häufig-Positionen,
 * Vorlagen, Skonto/§13b und optionaler Vermittler-Provision.
 *
 * Aus InvoicesPage.jsx ausgelagert (Refactoring Jun-2026).
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Car, Plus, TrendingUp, Receipt, X, ChevronDown, AlertTriangle, UserPlus, ShoppingCart, Search, Printer, ArrowLeftRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import api from "../../lib/api";
import { clampDiscountPct } from "../../lib/discountRules";
import WwsAddBar from "../../components/wms/WwsAddBar";
import LegalFooter from "../../components/LegalFooter";
import ArticleModal from "../wms/ArticleModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { fmtEur, fmtDate, ModalShell } from "./invoicesShared";
import { previewPdfResponse, previewPdfRequest, customerRecipient } from "../../lib/pdfPreview";
import { addressIssues } from "../../lib/addressCheck";
import { CustomerAddressQuickFix } from "../../components/CustomerAddressQuickFix";


const blankItem = () => ({
    description: "", quantity: 1, unit: "Stück", unit_price_net: 0,
    vat_rate: 19, is_differenzbesteuerung: false, purchase_price_gross: 0,
});

export function CreateInvoiceModal({ customers, onClose, onCreated, initialDirectSale = false, initialCustomerId = "", initialAutoScan = false }) {
    const [isDirectSale, setIsDirectSale] = useState(Boolean(initialDirectSale));
    const [customerId, setCustomerId] = useState(initialCustomerId || "");
    // Universal-Kundensuche (serverseitig über ALLE Kunden, nicht nur die geladenen).
    const custLabel = (c) => !c ? "" : (c.type === "business" && c.company_name
        ? `${c.company_name} — ${c.first_name || ""} ${c.last_name || ""}`.trim()
        : `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "—");
    const [selectedCustomer, setSelectedCustomer] = useState(
        () => (customers || []).find((c) => c.id === initialCustomerId) || null);
    const [custSearch, setCustSearch] = useState("");
    const [custResults, setCustResults] = useState([]);
    const [custSearching, setCustSearching] = useState(false);
    useEffect(() => {
        const term = custSearch.trim();
        if (!term) { setCustResults([]); return; }
        setCustSearching(true);
        const h = setTimeout(async () => {
            try {
                const { data } = await api.get(`/customers?q=${encodeURIComponent(term)}&limit=20`);
                setCustResults(data || []);
            } catch { setCustResults([]); }
            setCustSearching(false);
        }, 300);
        return () => clearTimeout(h);
    }, [custSearch]);
    const pickCustomer = (c) => { setSelectedCustomer(c); setCustomerId(c.id); setCustSearch(""); setCustResults([]); setCustCreating(false); };
    // Inline-Kundenanlage direkt im Rechnungsfluss (wie im Verschieben-Dialog).
    const [custCreating, setCustCreating] = useState(false);
    const [custNc, setCustNc] = useState({ type: "private", first_name: "", last_name: "", company_name: "", phone: "", email: "" });
    const [custNcBusy, setCustNcBusy] = useState(false);
    const createInvoiceCustomer = async () => {
        if (!custNc.first_name.trim() || !custNc.last_name.trim()) { toast.error("Vor- und Nachname Pflicht"); return; }
        setCustNcBusy(true);
        try {
            const payload = { ...custNc };
            ["company_name", "phone", "email"].forEach((k) => { if (!payload[k]) payload[k] = null; });
            const { data } = await api.post("/customers", payload);
            toast.success("Neuer Kunde angelegt");
            pickCustomer(data);
            setCustNc({ type: "private", first_name: "", last_name: "", company_name: "", phone: "", email: "" });
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
        setCustNcBusy(false);
    };
    // Jul-2026 (User-Wunsch): IMMER ohne Positionen starten (wie Direktverkauf) —
    // Positionen kommen über WWS-Suche/EAN-Scan oder „Artikel nicht gefunden".
    const [addrFixOpen, setAddrFixOpen] = useState(false);
    useEffect(() => { setAddrFixOpen(false); }, [customerId]);
    const [items, setItems] = useState([]);
    // Hinweis: Einleitungstext wird zentral im Händlerprofil
    // („Werkstatt / Teile / Zubehör" → Standard-Einleitungstext) gepflegt und
    // bei Finalisierung automatisch übernommen. Daher kein Feld mehr in der Maske.
    // Phase 4g.K (Feb 2026, User-Wunsch): Default-Zahlungsbedingung-Text entfernt — Feld startet leer, nichts mehr „voreingestellt".
    const [terms, setTerms] = useState("");
    // Phase 4e.I — Strukturierter Zahlungs-Code (Dropdown)
    // Feb-2026 (User-Wunsch): Zahlungsbedingungen immer mit Vorauswahl „EC-Karte" starten.
    const [paymentCode, setPaymentCode] = useState("ec");
    // Feb-2026: Skonto/Anzahlung/Reverse-Charge UI (Felder waren bereits Backend-seitig vorhanden).
    // Phase 4g.K: Default-Werte 0 entfernt — Felder starten leer, „0" nur als Placeholder.
    const [skontoPct, setSkontoPct] = useState("");
    const [skontoDays, setSkontoDays] = useState("");
    const [deposit, setDeposit] = useState("");
    const [reverseCharge, setReverseCharge] = useState(false);
    // Jul-2026 (User-Wunsch): EU-B2B-Kunde ⇒ steuerfreie innergem. Lieferung (netto).
    const EU_RC = ["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "GR", "EL", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "XI"];
    useEffect(() => {
        const cc = (selectedCustomer?.country || "DE").toUpperCase();
        const isEuB2b = EU_RC.includes(cc) && cc !== "DE" && (selectedCustomer?.type === "business" || !!selectedCustomer?.company_name);
        setReverseCharge(isEuB2b);
        if (isEuB2b) {
            toast.info("EU-Geschäftskunde erkannt — Rechnung wird als steuerfreie innergemeinschaftliche Lieferung erstellt. Positionen bitte mit 0 % USt erfassen und USt-IdNr. vorab via VIES prüfen!", { duration: 9000 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomer?.id]);
    // Phase 4g.P (Feb 2026, User-Wunsch): Skonto/Anzahlung/§13b-Block einklappbar, startet eingeklappt.
    const [extrasOpen, setExtrasOpen] = useState(false);
    // Jun-2026 (User-Wunsch): Vermittler-Provision einklappbar, startet geschlossen.
    const [referrerOpen, setReferrerOpen] = useState(false);
    // Phase 4b — Vermittler-Provision (optional)
    const [referrerQ, setReferrerQ] = useState("");
    const [referrerResults, setReferrerResults] = useState([]);
    const [referrer, setReferrer] = useState(null);
    const [referrerCommission, setReferrerCommission] = useState("");
    const [referrerNote, setReferrerNote] = useState("");
    // Phase 4e.I — Transaktions-Code für Vermittler
    const [referrerTxCode, setReferrerTxCode] = useState("");
    const [busy, setBusy] = useState(false);
    // Jun-2026 — Die WWS-Universalsuche (WwsAddBar) sucht jetzt SERVERSEITIG
    // (performant bei 50k+ Artikeln). Kein Vorab-Laden aller Artikel mehr nötig;
    // der EAN-Scan nutzt `/articles/by-ean` als Fallback.
    const [articles] = useState([]);
    // Garage-Fahrzeuge des gewählten Kunden (optional als 1. Rechnungsposition übernehmen)
    const [garageVehicles, setGarageVehicles] = useState([]);
    const [pickedVehicleId, setPickedVehicleId] = useState("");
    const [pickedCategory, setPickedCategory] = useState("");
    const [woSuggestion, setWoSuggestion] = useState(null); // letzter Werkstattauftrag
    const [dealerProfile, setDealerProfile] = useState(null);

    const [quickPositions, setQuickPositions] = useState([
        { label: "Zulassung", description: "Zulassung / Anmeldung" },
        { label: "Überführung", description: "Überführung / Transport" },
        { label: "HU/AU", description: "Hauptuntersuchung (HU/AU)" },
        { label: "Aufbereitung", description: "Fahrzeugaufbereitung" },
        { label: "Garantie", description: "Anschluss-Garantie" },
    ]);

    // Konfigurierte Schnellpositionen (mit Preisen) des Händlers laden
    useEffect(() => {
        let active = true;
        api.get("/users/me/dealer-profile")
            .then((r) => {
                if (!active) return;
                if (r.data) setDealerProfile(r.data);
                const qp = r.data?.invoice_quick_positions;
                if (Array.isArray(qp) && qp.length) setQuickPositions(qp);
            })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    // Auto-Vorschläge aus den am häufigsten genutzten Rechnungspositionen
    const [frequentPositions, setFrequentPositions] = useState([]);
    useEffect(() => {
        let active = true;
        api.get("/dealer/invoice-positions/frequent?limit=8")
            .then((r) => { if (active) setFrequentPositions(r.data?.positions || []); })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    // Jul-2026 (User-Wunsch) — Die 5 häufigsten Rechnungen als Dropdown
    // (ersetzt „Als Vorlage speichern" + Vorlagen-Pills).
    const [frequentSets, setFrequentSets] = useState([]);
    useEffect(() => {
        let active = true;
        api.get("/dealer/invoices/frequent-sets?limit=5")
            .then((r) => { if (active) setFrequentSets(r.data?.sets || []); })
            .catch(() => {});
        return () => { active = false; };
    }, []);
    const insertFrequentSet = (id) => {
        const s = frequentSets.find((x) => x.id === id);
        if (!s) return;
        (s.positions || []).forEach((p) => appendPosition(p));
        toast.success(`${(s.positions || []).length} Position(en) übernommen`);
    };

    useEffect(() => {
        if (!customerId || isDirectSale) { setGarageVehicles([]); setPickedVehicleId(""); setWoSuggestion(null); return; }
        let active = true;
        api.get(`/customers/${customerId}/vehicles`)
            .then((r) => { if (active) setGarageVehicles(r.data || []); })
            .catch(() => { if (active) setGarageVehicles([]); });
        return () => { active = false; };
    }, [customerId, isDirectSale]);

    const QUICK_POSITIONS = quickPositions;

    const appendPosition = (pos) => setItems((arr) => {
        const blankIdx = arr.findIndex((it) => !String(it.description || "").trim());
        const newItem = {
            description: pos.description, quantity: pos.quantity ?? 1, unit: pos.unit ?? "Stück",
            unit_price_net: pos.unit_price_net ?? 0, vat_rate: pos.vat_rate ?? 19,
            is_differenzbesteuerung: false, purchase_price_gross: 0,
        };
        if (blankIdx >= 0) return arr.map((it, i) => i === blankIdx ? newItem : it);
        return [...arr, newItem];
    });

    // Jun-2026 (User-Wunsch) — WWS-Artikel als Rechnungsposition übernehmen.
    // §25a ist NUR zulässig, wenn der Artikel die Differenzbesteuerung aktiviert
    // hat (`_diffAllowed`); Freitext-Positionen erlauben §25a wie bisher.
    const addArticlePosition = (art) => {
        if (!art) return;
        const net = Number(art.sell_price_net || (art.sell_price_gross / (1 + (art.vat_rate || 19) / 100)) || 0);
        const newItem = {
            description: art.name,
            quantity: 1,
            unit: art.unit || (art.category === "labour" ? "AW" : "Stück"),
            unit_price_net: Number(net.toFixed(2)),
            // Jul-2026 (User-Wunsch) — Preisschutz: vorgegebener VK ist Untergrenze
            _min_price_net: Number(net.toFixed(2)),
            vat_rate: art.vat_rate || 19,
            discount_percent: 0,
            is_differenzbesteuerung: false,
            purchase_price_gross: 0,
            article_id: art.id,
            article_sku: art.sku,
            _diffAllowed: !!art.is_differenzbesteuerung,
            // Bestands-Info für Lager-Warnung (nur Teile haben einen Lagerstand)
            _isPart: art.category !== "labour",
            _stock: Number(art.stock_qty ?? 0),
        };
        setItems((arr) => {
            const blankIdx = arr.findIndex((it) => !String(it.description || "").trim());
            if (blankIdx >= 0) return arr.map((it, i) => i === blankIdx ? newItem : it);
            return [...arr, newItem];
        });
        toast.success(`${art.name} hinzugefügt`);
        if (newItem._isPart) loadOrgStock(art.id);
    };
    // Aug-2026 (User-Wunsch) — Bestands-Warnung pro STANDORT: eigener Bestand +
    // andere Org-Standorte mit Bestand nachladen (für 1-Klick-Umlagerung).
    const loadOrgStock = async (articleId) => {
        try {
            const { data } = await api.get(`/articles/${articleId}/org-stock`);
            const self = (data.locations || []).find((l) => l.is_self);
            const others = (data.locations || []).filter((l) => !l.is_self && Number(l.stock_qty) > 0);
            setItems((arr) => arr.map((it) => it.article_id === articleId
                ? { ...it, _stock: Number(self?.stock_qty ?? it._stock ?? 0), _orgLocations: others }
                : it));
        } catch { /* org-stock optional */ }
    };
    const [transferringIdx, setTransferringIdx] = useState(null);
    const transferItem = async (idx, it, loc) => {
        const shortfall = Math.max(1, Math.ceil(Number(it.quantity) - Number(it._stock || 0)));
        const qty = Math.min(shortfall, Number(loc.stock_qty));
        setTransferringIdx(idx);
        try {
            const { data } = await api.post(`/articles/${it.article_id}/quick-transfer`, {
                from_dealer_id: loc.dealer_id, quantity: qty,
            });
            setItems((arr) => arr.map((x, i) => i === idx
                ? {
                    ...x, article_id: data.own_article_id, _stock: Number(data.own_stock),
                    _orgLocations: (x._orgLocations || []).map((l) => l.dealer_id === loc.dealer_id
                        ? { ...l, stock_qty: Number(data.source_stock) } : l).filter((l) => Number(l.stock_qty) > 0),
                }
                : x));
            toast.success(`${qty} ${it.unit || "Stück"} von ${loc.site_code} umgelagert — Bestand jetzt hier verfügbar`, {
                duration: 8000,
                action: {
                    label: "Begleitschein (PDF)",
                    onClick: () => previewPdfRequest(
                        api.get(`/quick-transfers/${data.transfer_id}/pdf`, { responseType: "blob" }),
                        `Umlagerung_${it.article_sku || ""}.pdf`),
                },
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Umlagerung fehlgeschlagen");
        }
        setTransferringIdx(null);
    };
    // Jul-2026 (User-Wunsch) — Kassen-Feedback: Bestätigungston + grüner Blitz
    // beim erfolgreichen EAN-Scan (Theke muss nicht auf den Monitor schauen).
    const [scanFlash, setScanFlash] = useState(false);
    const scanFeedback = () => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            const ctx = new Ctx();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = "sine"; o.frequency.value = 1400;
            g.gain.setValueAtTime(0.2, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
            o.start(); o.stop(ctx.currentTime + 0.15);
            o.onended = () => ctx.close();
        } catch { /* Audio blockiert */ }
        setScanFlash(true);
        setTimeout(() => setScanFlash(false), 400);
    };
    const addByEan = async (code) => {
        const ean = String(code || "").trim();
        if (!ean) return;
        let art = (articles || []).find((a) => a.is_active && String(a.ean || "").trim() === ean);
        if (!art) {
            try { const r = await api.get(`/articles/by-ean/${encodeURIComponent(ean)}`); art = r.data; } catch { /* nicht gefunden */ }
        }
        if (!art) { toast.error(`Kein Artikel mit EAN ${ean} gefunden.`); return; }
        addArticlePosition(art);
        scanFeedback();
    };
    // Jun-2026 (User-Wunsch c) — Bei „kein Treffer" im WWS-Suchfeld: entweder
    // vollwertigen WWS-Artikel anlegen (Dialog, landet im Lager + als Position)
    // oder schnelle freie Position übernehmen.
    const [newArticle, setNewArticle] = useState(null);
    const addFreeTextPosition = (text) => {
        appendPosition({ description: text, quantity: 1, unit: "Stück", unit_price_net: 0, vat_rate: 19 });
        toast.success("Freie Position hinzugefügt");
    };
    // Jun-2026 (User-Wunsch) — 1-Klick-Nachbestellung des fehlenden Teils beim
    // hinterlegten Lieferanten (Bestell-Entwurf), direkt aus dem Verkaufsvorgang.
    const [reorderingIdx, setReorderingIdx] = useState(null);
    const reorderItem = async (idx, it) => {
        if (!it.article_id) return;
        const shortfall = Math.max(1, Math.ceil(Number(it.quantity) - Number(it._stock || 0)));
        setReorderingIdx(idx);
        try {
            const { data } = await api.post("/purchase-orders/quick-reorder", { article_id: it.article_id, quantity: shortfall });
            toast.success(data._reorder_action === "appended"
                ? `Zu Bestellung ${data.order_number} hinzugefügt (${shortfall} ${it.unit || "Stück"})`
                : `Bestellung ${data.order_number} als Entwurf angelegt (${shortfall} ${it.unit || "Stück"})`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Nachbestellung fehlgeschlagen");
        }
        setReorderingIdx(null);
    };

    const handlePickVehicle = async (vid) => {
        setPickedVehicleId(vid);
        const v = garageVehicles.find((x) => x.id === vid);
        const cat = v?.category || "";
        setPickedCategory(cat);
        if (v) {
            const parts = [`${v.brand || ""} ${v.model || ""}`.trim()];
            if (v.first_registration) parts.push(`EZ ${fmtDate(v.first_registration)}`);
            if (v.vin) parts.push(`FIN ${v.vin}`);
            if (v.license_plate) parts.push(v.license_plate);
            if (v.mileage != null && v.mileage !== "") parts.push(`${Number(v.mileage).toLocaleString("de-DE")} km`);
            const desc = parts.filter(Boolean).join(" · ");
            setItems((arr) => arr.length
                ? arr.map((it, idx) => idx === 0 ? { ...it, description: desc } : it)
                : [{ ...blankItem(), description: desc }]);
            toast.success("Fahrzeug als 1. Position übernommen");
        }
        // Kontextsensitive „Häufig genutzt" je Fahrzeugtyp nachladen
        if (cat) {
            try {
                const fr = await api.get(`/dealer/invoice-positions/frequent?limit=8&category=${cat}`);
                const list = fr.data?.positions || [];
                if (list.length) setFrequentPositions(list);
            } catch { /* fällt auf globale Liste zurück */ }
        }
        // Vorschlags-Positionen aus letztem Werkstattauftrag laden
        setWoSuggestion(null);
        try {
            const r = await api.get(`/customers/${customerId}/vehicles/${vid}/suggested-positions`);
            setWoSuggestion(r.data?.workshop_order || null);
        } catch { setWoSuggestion(null); }
    };

    const importWorkshopOrder = () => {
        if (!woSuggestion?.positions?.length) return;
        woSuggestion.positions.forEach((p) => appendPosition(p));
        toast.success(`${woSuggestion.positions.length} Position(en) aus Auftrag ${woSuggestion.order_number || ""} übernommen`);
    };

    // Debounced Vermittler-Suche
    useEffect(() => {
        if (referrer) return;
        if (referrerQ.trim().length < 2) { setReferrerResults([]); return; }
        const handle = setTimeout(async () => {
            try {
                const r = await api.get(`/users/search-vermittlers?q=${encodeURIComponent(referrerQ)}`);
                setReferrerResults(r.data?.items || []);
            } catch (_e) {
                setReferrerResults([]);
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [referrerQ, referrer]);

    const setItem = (i, k, v) => {
        // Jul-2026 (User-Wunsch): Rabatt-Höchstsätze aus den Einstellungen anwenden.
        if (k === "discount_percent" && discountRules.length) {
            const it = items[i] || {};
            const cat = it._isPart === true ? "part" : it._isPart === false ? "labour" : null;
            const res = clampDiscountPct(discountRules, cat, it.quantity, it.unit_price_net, Number(v));
            if (res) { toast.error(`Rabatt begrenzt auf ${res.pct} % (Regel: ${res.ruleName})`); v = res.pct; }
        }
        // Jul-2026 (User-Wunsch) — WWS-Preisschutz: max. Rabatt auf Artikelpreise.
        if (k === "discount_percent" && (items[i] || {}).article_id && Number(v) > maxWwsDiscount) {
            toast.error(`Maximaler Rabatt auf Artikelpreise: ${maxWwsDiscount} % (WWS-Einstellungen)`);
            v = maxWwsDiscount;
        }
        setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
    };
    const addItem = () => setItems((arr) => [...arr, blankItem()]);
    const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

    // Jun-2026 (User-Wunsch): A4-Rechnung an der Theke automatisch drucken.
    // Jul-2026 (User-Wunsch): Statt Direktdruck immer die PDF-Vorschau öffnen —
    // dort stehen Drucken, E-Mail, WhatsApp und SMS zur Auswahl (wie beim
    // Direktverkauf über den QR-Code).
    const printInvoicePdf = async (id) => {
        try {
            const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
            previewPdfResponse(res, "Rechnung.pdf", { recipient: customerRecipient(selectedCustomer) });
        } catch { toast.error("PDF konnte nicht geöffnet werden"); }
    };

    const [previewMode, setPreviewMode] = useState(false);

    // Jul-2026 (User-Wunsch) — Gutschein-Einlösung NUR beim Rechnungschreiben.
    // Am Kunden hinterlegte (ausgegebene) Gutscheine werden zur 1-Klick-Einlösung angezeigt.
    const [voucherCode, setVoucherCode] = useState("");
    const [voucherApplied, setVoucherApplied] = useState(null);
    const [voucherBusy, setVoucherBusy] = useState(false);
    const [voucherRestHint, setVoucherRestHint] = useState(null);
    const [custVouchers, setCustVouchers] = useState([]);
    const [discountRules, setDiscountRules] = useState([]);
    useEffect(() => {
        api.get("/dealer/discount-rules").then((r) => setDiscountRules(r.data?.rules || [])).catch(() => {});
    }, []);
    // Jul-2026 (User-Wunsch) — WWS-Preisschutz: max. Rabatt auf vorgegebene
    // Artikelpreise aus den WWS-Einstellungen (Default 20 %).
    const [maxWwsDiscount, setMaxWwsDiscount] = useState(20);
    useEffect(() => {
        api.get("/wms/settings").then((r) => {
            const v = Number(r.data?.max_sale_discount_pct);
            if (!isNaN(v) && v > 0) setMaxWwsDiscount(v);
        }).catch(() => {});
    }, []);
    useEffect(() => {
        if (!customerId) { setCustVouchers([]); return; }
        let active = true;
        api.get(`/dealer/vouchers/by-customer/${customerId}`)
            .then((r) => { if (active) setCustVouchers(r.data?.vouchers || []); })
            .catch(() => { if (active) setCustVouchers([]); });
        return () => { active = false; };
    }, [customerId]);
    const applyVoucher = async (codeOverride = null) => {
        const code = String(codeOverride || voucherCode).trim().toUpperCase();
        if (!code) return;
        if (voucherApplied) { toast.error("Es wurde bereits ein Gutschein angewendet."); return; }
        const base = calcTotals().net;
        if (base <= 0) { toast.error("Bitte zuerst Positionen mit Betrag erfassen."); return; }
        setVoucherBusy(true);
        try {
            const { data } = await api.post("/dealer/vouchers/redeem", { code, base_net: Number(base.toFixed(2)) });
            const isEur = data.discount_type === "eur";
            const label = isEur ? fmtEur(data.discount_value) : `${data.discount_value || data.discount_pct} %`;
            const disc = Number(data.discount_net ?? (isEur ? data.discount_value : base * data.discount_pct / 100));
            setItems((arr) => [...arr, {
                description: `Gutschein ${code} (−${label})`,
                quantity: 1, unit: "Pauschal", unit_price_net: -Math.abs(Number(disc.toFixed(2))),
                vat_rate: 19, discount_percent: 0, is_differenzbesteuerung: false, purchase_price_gross: 0,
            }]);
            setVoucherApplied({ code, label, net: disc });
            // Jul-2026 (User-Wunsch): Restguthaben — wahlweise als NEUER Gutschein
            // (Einstellung) oder Hinweis, dass es verfällt.
            const rest = isEur ? Math.max(0, Number((Number(data.discount_value) - disc).toFixed(2))) : 0;
            if (data.remainder_voucher?.code) {
                setVoucherRestHint({ type: "new_voucher", ...data.remainder_voucher });
                toast.success(`Restguthaben ${fmtEur(data.remainder_voucher.amount)} als neuer Gutschein ${data.remainder_voucher.code} angelegt`);
            } else {
                setVoucherRestHint(rest > 0.009 ? { type: "forfeit", amount: rest } : null);
            }
            setVoucherCode("");
            setCustVouchers((v) => v.filter((x) => x.code !== code));
            toast.success(`Gutschein ${code} eingelöst: −${disc.toFixed(2)} €`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Gutschein ungültig oder abgelaufen");
        } finally { setVoucherBusy(false); }
    };
    // Jul-2026 (User-Wunsch): Gutschein wird beim Eintippen automatisch geprüft und
    // angewendet — kein „Einlösen"-Knopf mehr. Format: GEB-/GUT-XXXXXX.
    useEffect(() => {
        const code = voucherCode.trim().toUpperCase();
        if (voucherApplied || voucherBusy) return;
        if (!/^[A-Z]{2,4}-[A-Z0-9]{6}$/.test(code)) return;
        const t = setTimeout(() => applyVoucher(code), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voucherCode, items, voucherApplied, voucherBusy]);

    // Brutto-/Netto-/USt-Summen für die Vorschau (spiegelt die Backend-Logik:
    // §25a-Positionen ohne offenen USt-Ausweis, Rabatt auf Netto).
    const calcTotals = () => {
        let net = 0, vat = 0, gross = 0;
        items.forEach((it) => {
            const qty = Number(it.quantity) || 0;
            const unit = Number(it.unit_price_net) || 0;
            const disc = Number(it.discount_percent) || 0;
            const vr = Number(it.vat_rate) || 0;
            if (it.is_differenzbesteuerung) {
                const g = Math.round(qty * unit * 100) / 100;
                net += g; gross += g;
            } else {
                const ln = Math.round(qty * unit * (1 - disc / 100) * 100) / 100;
                const lv = Math.round(ln * vr) / 100;
                net += ln; vat += lv; gross += ln + lv;
            }
        });
        return { net: Math.round(net * 100) / 100, vat: Math.round(vat * 100) / 100, gross: Math.round(gross * 100) / 100 };
    };

    // Jul-2026 (User-Wunsch) — Direktverkauf nur bis 250 € brutto möglich
    // (Kleinbetragsrechnung §33 UStDV). Bei Überschreitung: Hinweis + automatisch
    // zur Kundendaten-Erfassung wechseln (Positionen bleiben erhalten).
    const DIRECT_SALE_LIMIT = 250;
    const [overLimit, setOverLimit] = useState(false);
    useEffect(() => {
        if (!isDirectSale) return;
        if (calcTotals().gross > DIRECT_SALE_LIMIT) {
            setOverLimit(true);
            setIsDirectSale(false);
            toast.warning("Betrag über 250 € — Direktverkauf nicht mehr möglich", {
                description: "Ab 250 € brutto sind Kundendaten Pflicht (§33 UStDV). Bitte jetzt den Kunden erfassen.",
                duration: 8000,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, isDirectSale]);

    // Jul-2026 (User-Wunsch) — Direktverkauf-Kasse: EC-Terminal + Sofort-Versand
    const [directDone, setDirectDone] = useState(null); // {id, number, gross}
    const [ecStatus, setEcStatus] = useState(null); // null | sending | sent | error
    const [ecPaymentId, setEcPaymentId] = useState(null); // SumUp-Terminal-Zahlung für Auto-„Bezahlt"
    // Jul-2026 (User-Wunsch): EC-Terminal-Button nur zeigen, wenn unter
    // Buchhaltung ein SumUp-Terminal per Pairing-Code gekoppelt wurde.
    const [terminalReady, setTerminalReady] = useState(false);
    useEffect(() => {
        api.get("/buchhaltung/ec/sumup/readers")
            .then((r) => setTerminalReady((r.data?.items || []).length > 0))
            .catch(() => setTerminalReady(false));
    }, []);
    const [sendEmailAddr, setSendEmailAddr] = useState("");
    const [sendWaPhone, setSendWaPhone] = useState("");
    const [sendBusy, setSendBusy] = useState(false);
    const sendToEcTerminal = async () => {
        const gross = calcTotals()?.gross || 0;
        if (gross <= 0) { toast.error("Betrag ist 0 — bitte zuerst Positionen erfassen"); return; }
        setEcStatus("sending");
        try {
            const r = await api.post("/buchhaltung/ec/sumup/terminal-checkout", { amount: gross, description: isDirectSale ? "Direktverkauf" : "Rechnung" });
            setEcPaymentId(r.data?.payment?.id || null);
            setEcStatus("sent");
            toast.success(`${fmtEur(gross)} an das EC-Terminal gesendet — Zahlung am Gerät bestätigen`);
        } catch (e) {
            setEcStatus("error");
            toast.error(e.response?.data?.detail || "EC-Terminal nicht erreichbar");
        }
    };
    // Jul-2026 (User-Wunsch) — Auto-„Bezahlt": nach Terminal-Bestätigung wird die
    // Rechnung automatisch als bezahlt verbucht (Polling; Fallback: Nightly-Sync).
    const watchInvoiceTerminalPayment = async (paymentId) => {
        for (let n = 0; n < 18; n += 1) {
            await new Promise((res) => setTimeout(res, 5000));
            try {
                const r = await api.post(`/buchhaltung/ec/sumup/terminal-payments/${paymentId}/check`);
                const st = r.data?.status;
                if (st === "booked") { toast.success("EC-Zahlung bestätigt — Rechnung automatisch als bezahlt markiert"); return; }
                if (st === "failed") { toast.error("Terminal-Zahlung fehlgeschlagen oder abgebrochen"); return; }
            } catch { return; }
        }
    };
    const linkEcPaymentToInvoice = async (invoiceId) => {
        if (!ecPaymentId) return;
        try {
            const r = await api.post(`/buchhaltung/ec/sumup/terminal-payments/${ecPaymentId}/link-invoice`, { invoice_id: invoiceId });
            if (r.data?.booked) toast.success("EC-Zahlung bestätigt — Rechnung als bezahlt markiert");
            else watchInvoiceTerminalPayment(ecPaymentId);
        } catch { /* Nightly-Sync verbucht als Fallback */ }
    };
    const sendDirectEmail = async () => {
        const em = sendEmailAddr.trim().toLowerCase();
        if (!em || !em.includes("@")) { toast.error("Bitte gültige E-Mail-Adresse eingeben"); return; }
        setSendBusy(true);
        try {
            const r = await api.post(`/invoices/${directDone.id}/send-email`, { to_email: em });
            toast.success(`Rechnung per E-Mail an ${em} gesendet${r.data?.mocked ? " (Testmodus)" : ""}`);
        } catch (e) { toast.error(e.response?.data?.detail || "E-Mail-Versand fehlgeschlagen"); }
        setSendBusy(false);
    };
    // Jul-2026 (User-Wunsch): Kassenbon drucken + QR-Code fürs Kundenhandy.
    const [shareQrPath, setShareQrPath] = useState(null);
    useEffect(() => {
        if (!directDone?.id) { setShareQrPath(null); return; }
        api.post(`/invoices/${directDone.id}/share-link`)
            .then((r) => setShareQrPath(r.data.path))
            .catch(() => {});
    }, [directDone?.id]);

    const printReceipt = async () => {
        try {
            const r = await api.get(`/invoices/${directDone.id}/receipt.pdf`, { responseType: "blob" });
            // Jul-2026 (Bugfix „schwarzer Bildschirm am Handy") — statt window.open(blob)
            // die integrierte PDF-Vorschau nutzen (funktioniert auf PC UND Handy).
            previewPdfResponse(r, `Bon_${directDone.number || "Rechnung"}.pdf`);
        } catch { toast.error("Bon konnte nicht erstellt werden"); }
    };

    const sendDirectWhatsApp = async () => {
        setSendBusy(true);
        try {
            const r = await api.post(`/invoices/${directDone.id}/share-link`);
            const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.path}`;
            const text = encodeURIComponent(`Vielen Dank für Ihren Einkauf! Ihre Rechnung ${directDone.number}: ${url}`);
            const phone = sendWaPhone.replace(/[^0-9+]/g, "").replace(/^\+/, "").replace(/^00/, "");
            window.open(phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`, "_blank");
        } catch (e) { toast.error(e.response?.data?.detail || "Link konnte nicht erstellt werden"); }
        setSendBusy(false);
    };

    const buildPayload = () => ({
        customer_id: isDirectSale ? null : customerId,
        is_direct_sale: isDirectSale,
        vehicle_category: pickedCategory || null,
        items: items.map((it) => ({
            ...it,
            quantity: Number(it.quantity),
            unit_price_net: Number(it.unit_price_net),
            vat_rate: Number(it.vat_rate),
            discount_percent: Number(it.discount_percent || 0),
            purchase_price_gross: it.is_differenzbesteuerung ? Number(it.purchase_price_gross) : null,
        })),
        intro_text: null,
        payment_terms: terms || null,
        payment_terms_code: paymentCode || null,
        skonto_percent: Number(skontoPct) || 0,
        skonto_days: Number(skontoDays) || 0,
        deposit_paid: Number(deposit) || 0,
        reverse_charge: reverseCharge,
        referrer_user_id: referrer?.id || null,
        referrer_commission_eur: referrer && referrerCommission ? Number(referrerCommission) : null,
        referrer_note: referrer && referrerNote ? referrerNote : null,
        referrer_transaction_code: referrer && referrerTxCode ? referrerTxCode : null,
    });

    const validate = () => {
        if (!isDirectSale && !customerId) { toast.error("Bitte Kunde wählen oder Direktverkauf aktivieren"); return false; }
        // Jul-2026 (User-Wunsch): Direktverkauf braucht keine Kunden-Warnung —
        // aber eine gewählte Zahlungsart (z. B. EC-Karte) vor der Erstellung.
        if (isDirectSale && paymentCode !== "ec") { toast.error("Direktverkauf ist nur mit EC-Karte möglich"); return false; }
        if (isDirectSale && calcTotals().gross > DIRECT_SALE_LIMIT) { toast.error("Direktverkauf nur bis 250 € brutto möglich (§33 UStDV) — bitte Kundendaten erfassen"); return false; }
        if (items.length === 0) { toast.error("Bitte mindestens eine Position über die Suche/EAN-Scan oder „Artikel nicht gefunden“ hinzufügen"); return false; }
        if (items.some((it) => !it.description || Number(it.unit_price_net) === 0)) { toast.error("Bitte alle Positionen ausfüllen"); return false; }
        // Jul-2026 (User-Wunsch) — WWS-Preisschutz: vorgegebener Artikelpreis ist Untergrenze.
        const under = items.find((it) => it._min_price_net != null && Number(it.unit_price_net) < Number(it._min_price_net) - 0.005);
        if (under) { toast.error(`„${under.description}": Vorgegebener Artikelpreis darf nicht unterschritten werden (mind. ${fmtEur(under._min_price_net)}) — bitte Rabatt nutzen (max. ${maxWwsDiscount} %).`); return false; }
        // Jul-2026 (User-Wunsch): Nicht vorrätige Artikel erfordern eine Bestellung
        // mit Kundendaten — Direktverkauf ist dann nicht möglich.
        if (isDirectSale && items.some((it) => it._isPart && it._stock != null && Number(it.quantity) > Number(it._stock))) {
            toast.error("Artikel nicht am Lager: Bestellung erfordert Kundendaten — Direktverkauf nicht möglich. Bitte Kunden anlegen/auswählen.");
            return false;
        }
        // Jul-2026 (User-Wunsch): gesperrte Kunden — keine neuen Rechnungen.
        if (!isDirectSale && selectedCustomer?.blocked) {
            toast.error(`Kunde ist gesperrt${selectedCustomer.blocked_reason ? ` (${selectedCustomer.blocked_reason})` : ""} — keine Rechnung möglich.`);
            return false;
        }
        return true;
    };

    // Jun-2026 (User-Wunsch): KEINE Entwürfe mehr. Der Hauptbutton öffnet eine
    // Vorschau; erst „Rechnung erstellen" legt an UND finalisiert sofort
    // (lückenlose Nummer). Direktverkauf (POS) bleibt 1-Klick ohne Vorschau.
    const createInvoice = async () => {
        if (!validate()) return;
        setBusy(true);
        try {
            const { data: created } = await api.post("/invoices", buildPayload());
            const { data: fin } = await api.post(`/invoices/${created.id}/finalize`);
            toast.success(`Rechnung ${fin.invoice_number || ""} erstellt`);
            // Jul-2026 — ans Terminal gesendete EC-Zahlung mit der Rechnung
            // verknüpfen → Auto-„Bezahlt" nach Terminal-Bestätigung.
            linkEcPaymentToInvoice(created.id);
            if (isDirectSale) {
                printInvoicePdf(created.id);
                setPreviewMode(false);
                setDirectDone({ id: created.id, number: fin.invoice_number || "", gross: calcTotals()?.gross || 0 });
            } else {
                onCreated();
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };

    const submit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        setPreviewMode(true);
    };

    return (
        <ModalShell onClose={onClose} title="Neue Rechnung" wide testid="new-invoice-modal">
            {scanFlash && (
                <div className="fixed inset-0 z-[90] bg-emerald-400/50 pointer-events-none animate-pulse" data-testid="inv-scan-flash" />
            )}
            {directDone && (
                <div className="space-y-4" data-testid="inv-direct-done">
                    <div className="bg-emerald-50 border-l-4 border-emerald-500 px-4 py-3 rounded-sm">
                        <div className="font-black text-emerald-900">✓ Rechnung {directDone.number} erstellt — {fmtEur(directDone.gross)}</div>
                        <div className="text-xs text-emerald-800 mt-0.5">Der Beleg-Druck wurde geöffnet. Rechnung jetzt direkt an den Kunden senden?</div>
                        {ecPaymentId && (
                            <div className="text-[11px] text-emerald-800 mt-1" data-testid="inv-direct-autopaid-hint">
                                💳 Sobald die EC-Zahlung am Terminal bestätigt ist, wird die Rechnung <strong>automatisch als bezahlt</strong> markiert (spätestens beim nächtlichen SumUp-Abgleich).
                            </div>
                        )}
                    </div>
                    {/* Jul-2026 (User-Wunsch): Bon drucken + QR-Code fürs Kundenhandy. */}
                    <div className="flex gap-3 flex-wrap items-stretch">
                        <div className="rounded-sm border border-gray-200 p-3 flex-1 min-w-[220px] flex flex-col justify-between gap-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Kassenbon</label>
                            <p className="text-[10px] text-gray-500">80-mm-Bonformat — öffnet als PDF, druckbar auf jedem Bondrucker.</p>
                            <Button type="button" onClick={printReceipt}
                                className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase tracking-wider" data-testid="inv-direct-receipt-btn">
                                <Printer className="w-4 h-4 mr-1.5" /> Bon drucken
                            </Button>
                        </div>
                        <div className="rounded-sm border border-gray-200 p-3 flex flex-col items-center gap-1.5" data-testid="inv-direct-qr-box">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Rechnung aufs Kundenhandy</label>
                            {shareQrPath ? (
                                <>
                                    <QRCodeSVG value={`${process.env.REACT_APP_BACKEND_URL}${shareQrPath}`} size={110} level="M" data-testid="inv-direct-qr" />
                                    <p className="text-[10px] text-gray-500">Kunde scannt den Code und lädt die Rechnung als PDF.</p>
                                </>
                            ) : (
                                <p className="text-[10px] text-gray-400 py-8">QR-Code wird erstellt…</p>
                            )}
                        </div>
                    </div>
                    <div className="rounded-sm border border-gray-200 p-3 space-y-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Per E-Mail senden</label>
                        <div className="flex gap-2">
                            <Input type="email" placeholder="E-Mail-Adresse des Kunden" value={sendEmailAddr}
                                onChange={(e) => setSendEmailAddr(e.target.value)} className="rounded-sm" data-testid="inv-direct-email" />
                            <Button type="button" onClick={sendDirectEmail} disabled={sendBusy}
                                className="bg-[#111827] hover:bg-black text-white rounded-sm shrink-0" data-testid="inv-direct-email-send">
                                E-Mail senden
                            </Button>
                        </div>
                    </div>
                    <div className="rounded-sm border border-gray-200 p-3 space-y-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Per WhatsApp senden</label>
                        <div className="flex gap-2">
                            <Input placeholder="Handynummer (z. B. 0176… oder +49…)" value={sendWaPhone}
                                onChange={(e) => setSendWaPhone(e.target.value)} className="rounded-sm" data-testid="inv-direct-wa-phone" />
                            <Button type="button" onClick={sendDirectWhatsApp} disabled={sendBusy}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm shrink-0" data-testid="inv-direct-wa-send">
                                WhatsApp öffnen
                            </Button>
                        </div>
                        <p className="text-[10px] text-gray-500">Öffnet WhatsApp mit einem sicheren Rechnungs-Link — ohne Nummer wird der Kontakt in WhatsApp gewählt.</p>
                    </div>
                    <div className="flex justify-end">
                        <Button type="button" onClick={() => { setDirectDone(null); onCreated(); }}
                            className="bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid="inv-direct-done-btn">
                            Fertig
                        </Button>
                    </div>
                </div>
            )}
            {!previewMode && !directDone && (
            <form onSubmit={submit} className="space-y-4">
                <div>
                    {/* Jul-2026 (User-Wunsch): „Kunde *" + Direktverkauf-Checkbox in EINER Zeile. */}
                    <div className="flex items-center gap-4 flex-wrap mb-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Kunde *</label>
                        <label className="inline-flex items-center gap-2 text-xs">
                            <input type="checkbox" checked={isDirectSale} onChange={(e) => { const on = e.target.checked; if (on && calcTotals().gross > DIRECT_SALE_LIMIT) { toast.error("Direktverkauf nur bis 250 € brutto möglich (§33 UStDV) — bitte Kundendaten erfassen"); return; } setIsDirectSale(on); if (on) { setCustomerId(""); setSelectedCustomer(null); setCustSearch(""); setCustCreating(false); setOverLimit(false); setPaymentCode("ec"); setItems((arr) => arr.filter((it) => String(it.description || "").trim())); } }} className="w-4 h-4 accent-[#FFCC00]" data-testid="inv-direct-sale" />
                            <strong>Direktverkauf</strong> (Laufkundschaft, ohne Kundendaten)
                        </label>
                    </div>
                    {overLimit && !isDirectSale && !customerId && (
                        <div className="mb-2 text-xs font-bold text-amber-900 bg-amber-50 border-l-4 border-amber-500 px-3 py-2 rounded-sm" data-testid="inv-over-limit-hint">
                            Betrag über 250 € — Kleinbetragsrechnung ohne Kundendaten nicht mehr zulässig (§33 UStDV). Bitte den Kunden suchen oder neu anlegen; die erfassten Positionen bleiben erhalten.
                        </div>
                    )}
                    {!isDirectSale && (
                        selectedCustomer ? (
                            <div>
                            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-sm" data-testid="inv-customer-selected">
                                <span>
                                    <b>{custLabel(selectedCustomer)}</b>
                                    {selectedCustomer.customer_number ? <span className="text-gray-400 text-xs"> · Kd.-Nr. {selectedCustomer.customer_number}</span> : null}
                                    {selectedCustomer.city ? <span className="text-gray-400 text-xs"> · {selectedCustomer.city}</span> : null}
                                    {custVouchers.length > 0 && !voucherApplied && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-pink-100 border border-pink-300 text-pink-800 px-1.5 py-0.5 rounded-full" data-testid="inv-customer-voucher-badge">
                                            🎁 {custVouchers.length} Gutschein{custVouchers.length !== 1 ? "e" : ""} offen
                                        </span>
                                    )}
                                </span>
                                <button type="button" onClick={() => { setSelectedCustomer(null); setCustomerId(""); }} className="text-xs text-gray-500 hover:text-gray-800" data-testid="inv-customer-change">ändern</button>
                            </div>
                            {addressIssues(selectedCustomer).length > 0 && (
                                <div className="mt-2 text-[11px] font-bold rounded-sm px-2 py-1.5 border bg-amber-50 border-amber-300 text-amber-900" data-testid="inv-customer-addr-warning">
                                    <div className="flex items-start gap-1.5">
                                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                        <span className="flex-1">
                                            Anschrift unvollständig — es fehlt: <b>{addressIssues(selectedCustomer).join(", ")}</b>.
                                            Bitte vor dem Festschreiben ergänzen (Rechnung wird sonst mit unvollständiger Adresse gedruckt).
                                        </span>
                                        {!addrFixOpen && (
                                            <button type="button" onClick={() => setAddrFixOpen(true)}
                                                className="shrink-0 px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black text-[10px] font-bold"
                                                data-testid="inv-addr-quickfix-btn">
                                                Jetzt ergänzen
                                            </button>
                                        )}
                                    </div>
                                    {addrFixOpen && (
                                        <CustomerAddressQuickFix customer={selectedCustomer}
                                            onSaved={(u) => { setSelectedCustomer(u); setAddrFixOpen(false); }}
                                            onClose={() => setAddrFixOpen(false)} />
                                    )}
                                </div>
                            )}
                            {(selectedCustomer.blocked || selectedCustomer.marketing_blocked) && (
                                <div className={`mt-2 text-[11px] font-bold rounded-sm px-2 py-1.5 border ${selectedCustomer.blocked ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800"}`} data-testid="inv-customer-blocked-warning">
                                    {selectedCustomer.blocked
                                        ? `⛔ Kunde gesperrt${selectedCustomer.blocked_reason ? ` — ${selectedCustomer.blocked_reason}` : ""}. Keine Rechnung möglich.`
                                        : "📵 DSGVO-Widerspruch: KEINE Werbung oder automatische Kontaktaufnahme per E-Mail, WhatsApp oder SMS."}
                                </div>
                            )}
                            {reverseCharge && (
                                <div className="mt-2 text-[11px] font-bold rounded-sm px-2 py-1.5 border bg-blue-50 border-blue-300 text-blue-900" data-testid="inv-reverse-charge-banner">
                                    🇪🇺 Steuerfreie innergemeinschaftliche Lieferung (§ 4 Nr. 1b / § 6a UStG): Rechnung ohne deutsche USt — Positionen mit <b>0 % USt</b> erfassen. Pflicht: gültige USt-IdNr. (VIES-geprüft){selectedCustomer?.ust_id ? ` — hinterlegt: ${selectedCustomer.ust_id}` : " — FEHLT im Kundenstamm!"}
                                </div>
                            )}
                            </div>
                        ) : (
                            <div>
                                {/* Jul-2026 (User-Wunsch): Suche + „Neuen Kunden anlegen" in EINER Zeile. */}
                                <div className="flex items-center gap-2">
                                    <Input
                                        autoFocus
                                        placeholder="Kunde suchen (Name, Firma, Kd.-Nr., E-Mail, Adresse…)"
                                        value={custSearch}
                                        onChange={(e) => setCustSearch(e.target.value)}
                                        className="rounded-sm focus-visible:ring-emerald-500 flex-1"
                                        data-testid="inv-customer-search"
                                    />
                                    {!custCreating && (
                                        <button type="button" onClick={() => setCustCreating(true)} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-[#111827] border border-dashed border-gray-300 rounded-sm px-3 h-9 hover:border-[#111827] hover:bg-gray-50" data-testid="inv-customer-new">
                                            <UserPlus className="w-3.5 h-3.5" /> Neuen Kunden anlegen
                                        </button>
                                    )}
                                </div>
                                {custSearch.trim() ? (
                                    <div className="mt-1 border border-gray-200 rounded-sm max-h-52 overflow-y-auto divide-y divide-gray-100" data-testid="inv-customer-results">
                                        {custSearching ? <div className="p-3 text-xs text-gray-400">Suche…</div>
                                            : custResults.length === 0 ? <div className="p-3 text-xs text-gray-400 italic">Keine Treffer — lege unten einen neuen Kunden an.</div>
                                                : custResults.map((c) => (
                                                    <button key={c.id} type="button" onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" data-testid={`inv-customer-result-${c.id}`}>
                                                        <span className="font-medium">{custLabel(c)}</span>
                                                        {c.customer_number ? <span className="text-gray-400 text-xs"> · Kd.-Nr. {c.customer_number}</span> : null}
                                                        {c.city ? <span className="text-gray-400 text-xs"> · {c.city}</span> : null}
                                                    </button>
                                                ))}
                                    </div>
                                ) : null}
                                {custCreating && (
                                    <div className="mt-2 border border-gray-200 rounded-sm p-3 space-y-2" data-testid="inv-customer-create-panel">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Neuen Kunden anlegen</span>
                                            <button type="button" onClick={() => setCustCreating(false)} className="text-xs text-gray-500 hover:text-gray-800" data-testid="inv-customer-create-cancel">Abbrechen</button>
                                        </div>
                                        <Select value={custNc.type} onValueChange={(v) => setCustNc({ ...custNc, type: v })}>
                                            <SelectTrigger className="rounded-sm h-9" data-testid="inv-customer-nc-type"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="private">Privatkunde</SelectItem>
                                                <SelectItem value="business">Geschäftskunde</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {custNc.type === "business" && (
                                            <Input placeholder="Firmenname" value={custNc.company_name} onChange={(e) => setCustNc({ ...custNc, company_name: e.target.value })} className="rounded-sm" data-testid="inv-customer-nc-company" />
                                        )}
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input placeholder="Vorname *" value={custNc.first_name} onChange={(e) => setCustNc({ ...custNc, first_name: e.target.value })} className="rounded-sm" data-testid="inv-customer-nc-first" />
                                            <Input placeholder="Nachname *" value={custNc.last_name} onChange={(e) => setCustNc({ ...custNc, last_name: e.target.value })} className="rounded-sm" data-testid="inv-customer-nc-last" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input placeholder="Telefon" value={custNc.phone} onChange={(e) => setCustNc({ ...custNc, phone: e.target.value })} className="rounded-sm" data-testid="inv-customer-nc-phone" />
                                            <Input type="email" placeholder="E-Mail" value={custNc.email} onChange={(e) => setCustNc({ ...custNc, email: e.target.value })} className="rounded-sm" data-testid="inv-customer-nc-email" />
                                        </div>
                                        <Button type="button" onClick={createInvoiceCustomer} disabled={custNcBusy} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="inv-customer-nc-submit">
                                            {custNcBusy ? "Lege an…" : "Anlegen & auswählen"}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )
                    )}
                    {/* Jul-2026 (User-Wunsch): §33-Hinweis NUR zeigen, wenn 250 € überschritten. */}
                    {isDirectSale && calcTotals().gross > DIRECT_SALE_LIMIT && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm">
                            ℹ Rechnung ohne Kundenadresse — nur bis <strong>250 € brutto</strong> möglich (§33 UStDV) und ausschließlich per <strong>EC-Karte</strong>. Darüber wird automatisch zur Kundenerfassung gewechselt.
                        </div>
                    )}
                    {!isDirectSale && customerId && garageVehicles.length > 0 && (
                        <div className="mt-2" data-testid="inv-garage-picker">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
                                <Car className="w-3.5 h-3.5" /> Fahrzeug aus Garage als 1. Position
                            </label>
                            <Select value={pickedVehicleId} onValueChange={handlePickVehicle}>
                                <SelectTrigger className="rounded-sm mt-1" data-testid="inv-garage-vehicle"><SelectValue placeholder="Fahrzeug übernehmen…" /></SelectTrigger>
                                <SelectContent>
                                    {garageVehicles.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {`${v.brand || ""} ${v.model || ""}`.trim()}{v.license_plate ? ` · ${v.license_plate}` : (v.vin ? ` · FIN ${v.vin}` : "")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {!isDirectSale && customerId && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="inv-quick-positions">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mr-1">Vorschläge:</span>
                            {QUICK_POSITIONS.map((p) => (
                                <button key={p.label} type="button" onClick={() => appendPosition(p)}
                                    className="text-[11px] font-bold px-2 py-1 rounded-full border border-gray-300 text-gray-700 hover:border-[#111827] hover:bg-gray-50 inline-flex items-center gap-1"
                                    data-testid={`inv-quick-${p.label}`}>
                                    <Plus className="w-3 h-3" /> {p.label}{Number(p.unit_price_net) > 0 ? ` · ${fmtEur(p.unit_price_net)}` : ""}
                                </button>
                            ))}
                        </div>
                    )}
                    {!isDirectSale && customerId && frequentPositions.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="inv-frequent-positions">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mr-1 inline-flex items-center gap-1">
                                <TrendingUp className="w-3.5 h-3.5" /> Häufig genutzt:
                            </span>
                            {frequentPositions.map((p, i) => (
                                <button key={`${p.description}-${i}`} type="button" onClick={() => appendPosition(p)}
                                    className="text-[11px] font-bold px-2 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1"
                                    title={`${p.count}× verwendet`}
                                    data-testid={`inv-frequent-${i}`}>
                                    <Plus className="w-3 h-3" /> {p.label}{Number(p.unit_price_net) > 0 ? ` · ${fmtEur(p.unit_price_net)}` : ""}
                                </button>
                            ))}
                        </div>
                    )}
                    {!isDirectSale && pickedVehicleId && woSuggestion?.positions?.length > 0 && (
                        <button type="button" onClick={importWorkshopOrder}
                            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-300 rounded-sm py-2 hover:bg-emerald-100"
                            data-testid="inv-import-workshop">
                            <Receipt className="w-3.5 h-3.5" /> Letzten Werkstattauftrag {woSuggestion.order_number ? `#${woSuggestion.order_number}` : ""} übernehmen ({woSuggestion.positions.length} Pos.)
                        </button>
                    )}
                </div>

                <div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        {/* Jul-2026 (User-Wunsch): „Als Vorlage speichern" entfernt —
                            stattdessen Dropdown mit den 5 häufigsten Rechnungen. */}
                        {!isDirectSale && frequentSets.length > 0 && (
                            <Select value="" onValueChange={insertFrequentSet}>
                                <SelectTrigger className="rounded-sm w-72" data-testid="inv-frequent-sets">
                                    <SelectValue placeholder="Häufige Rechnungen übernehmen…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {frequentSets.map((s) => (
                                        <SelectItem key={s.id} value={s.id} data-testid={`inv-frequent-set-${s.id}`}>
                                            {s.label} · {s.count}×
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                    <div className="space-y-2 mt-1">
                        {/* Jun-2026 (User-Wunsch) — WWS-Suche + EAN-Scan gut sichtbar hervorgehoben. */}
                        <div className="rounded-sm border-2 border-[#FFCC00] bg-[#FFFBEB] p-3" data-testid="inv-wws-prominent">
                            <WwsAddBar
                                header={<div className="text-xs font-black uppercase tracking-wider text-[#111827] flex items-center gap-1.5"><Search className="w-4 h-4" /> Artikel / Leistung suchen</div>}
                                headerActions={!isDirectSale ? (
                                    <Button type="button" size="sm" variant="outline" onClick={addItem} className="h-8 rounded-sm text-xs font-bold" data-testid="item-add">
                                        <Plus className="w-3.5 h-3.5 mr-1" /> Artikel nicht gefunden
                                    </Button>
                                ) : null}
                                articles={articles} onAddArticle={addArticlePosition} onAddEan={addByEan} onCreateArticle={(q) => setNewArticle(/^\d{8,14}$/.test(q.trim()) ? { ean: q.trim(), category: "part" } : { name: q })} onAddFreeText={addFreeTextPosition} testidPrefix="inv-wws" autoScan={initialAutoScan && isDirectSale} />
                        </div>
                        {items.length === 0 && (
                            <div className="text-center text-xs text-gray-500 py-4 border border-dashed border-gray-200 rounded-sm" data-testid="inv-no-items">
                                Noch keine Positionen. Über die Suche/EAN oben hinzufügen{!isDirectSale ? ' oder „Artikel nicht gefunden“ für eine Freitext-Zeile' : ""}.
                            </div>
                        )}
                        {items.map((it, i) => (
                            <div key={i} className="bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid={`inv-item-${i}`}>
                                <div className="flex gap-2 items-start mb-2">
                                    <div className="flex-1">
                                        <Input placeholder="Beschreibung (z. B. BMW R 1250 GS · EZ 05/2022)" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="rounded-sm w-full" data-testid={`item-desc-${i}`} />
                                        {it.article_sku && (
                                            <span className="text-[10px] text-gray-500 mt-0.5 inline-block" data-testid={`item-sku-${i}`}>WWS · {it.article_sku}</span>
                                        )}
                                    </div>
                                    <Button type="button" size="sm" variant="outline" onClick={() => removeItem(i)} className="rounded-sm" data-testid={`item-remove-${i}`}>
                                        <X className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                                {/* Jul-2026 (User-Wunsch): USt-Feld ausgeblendet (Standard bleibt intern),
                                    Rabatt rückt an dessen Platz — Bezeichnung als Platzhalter im Feld. */}
                                <div className="grid grid-cols-4 gap-2">
                                    <Input type="number" min="0" step="0.5" placeholder="Menge" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="rounded-sm" data-testid={`item-qty-${i}`} />
                                    <Input placeholder="Einheit" value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="rounded-sm" data-testid={`item-unit-${i}`} />
                                    <Input type="number" min="0" step="0.01" placeholder={it.is_differenzbesteuerung ? "Verkaufspreis brutto" : "Einzelpreis netto"} value={it.unit_price_net} onChange={(e) => setItem(i, "unit_price_net", e.target.value)} onBlur={() => { if (it._min_price_net != null && Number(it.unit_price_net) < Number(it._min_price_net)) { setItem(i, "unit_price_net", it._min_price_net); toast.error(`Vorgegebener Artikelpreis (${fmtEur(it._min_price_net)}) darf nicht unterschritten werden — Preissenkung nur über Rabatt (max. ${maxWwsDiscount} %). Nach oben ist der Preis frei anpassbar.`); } }} className="rounded-sm" data-testid={`item-price-${i}`} />
                                    <Input type="number" min="0" max="100" step="1" placeholder="Rabatt %" value={it.discount_percent || ""} onChange={(e) => setItem(i, "discount_percent", e.target.value)} className="rounded-sm" data-testid={`item-discount-${i}`} />
                                </div>
                                {it._isPart && it._stock != null && Number(it.quantity) > Number(it._stock) && (
                                    <div className="mt-2 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid={`item-stock-warning-${i}`}>
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                            <span className="flex-1">
                                                Nur {Number(it._stock).toLocaleString("de-DE")} von {Number(it.quantity).toLocaleString("de-DE")} an diesem Standort auf Lager{Number(it._stock) <= 0 ? " (nicht vorrätig)" : ""}.
                                                {isDirectSale && !(it._orgLocations || []).length && (
                                                    <span className="block text-red-700" data-testid={`item-stock-directsale-block-${i}`}>
                                                        Bestellung erfordert Kundendaten — Direktverkauf nicht möglich. Bitte Kunden anlegen/auswählen.
                                                    </span>
                                                )}
                                            </span>
                                            {!isDirectSale && (
                                                <button type="button" onClick={() => reorderItem(i, it)} disabled={reorderingIdx === i}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black disabled:opacity-50 shrink-0"
                                                    data-testid={`item-reorder-${i}`}>
                                                    <ShoppingCart className="w-3 h-3" /> {reorderingIdx === i ? "…" : "Nachbestellen"}
                                                </button>
                                            )}
                                        </div>
                                        {(it._orgLocations || []).length > 0 && (
                                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-5" data-testid={`item-org-stock-${i}`}>
                                                <span className="text-emerald-800">An anderen Standorten verfügbar:</span>
                                                {it._orgLocations.slice(0, 4).map((loc) => (
                                                    <button key={loc.dealer_id} type="button" onClick={() => transferItem(i, it, loc)} disabled={transferringIdx === i}
                                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                                        data-testid={`item-transfer-${i}-${loc.site_code}`}>
                                                        <ArrowLeftRight className="w-3 h-3" />
                                                        {transferringIdx === i ? "…" : `Von ${loc.site_code} umlagern (${Number(loc.stock_qty).toLocaleString("de-DE")} verfügbar)`}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Jul-2026 (User-Wunsch): §25a nur sichtbar bei USt 0 %
                                    (oder wenn bereits aktiviert). */}
                                {it._diffAllowed !== false && (Number(it.vat_rate) === 0 || it.is_differenzbesteuerung) && (
                                    <div className="mt-2 flex items-center gap-2">
                                        <input type="checkbox" id={`d25-${i}`} checked={it.is_differenzbesteuerung} onChange={(e) => setItem(i, "is_differenzbesteuerung", e.target.checked)} data-testid={`item-25a-${i}`} />
                                        <label htmlFor={`d25-${i}`} className="text-xs font-bold text-gray-700">§25a Differenzbesteuerung anwenden</label>
                                        {it.is_differenzbesteuerung && (
                                            <Input type="number" min="0" step="0.01" placeholder="Einkaufspreis brutto" value={it.purchase_price_gross} onChange={(e) => setItem(i, "purchase_price_gross", e.target.value)} className="rounded-sm ml-2 max-w-[180px]" data-testid={`item-purchase-${i}`} />
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* §13b/Skonto/Anzahlung-Block entfernt (User Jun-2026) */}

                {/* Phase 4b — Vermittler-Provision (Jun-2026: einklappbar, geschlossen starten) */}
                <div className="bg-violet-50 border border-violet-200 rounded-sm p-3 space-y-2" data-testid="inv-referrer-block">
                    <button type="button" onClick={() => setReferrerOpen((v) => !v)}
                        className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wider text-violet-900"
                        data-testid="inv-referrer-toggle" aria-expanded={referrerOpen}>
                        <span className="flex items-center gap-2">
                            Vermittler-Provision (optional)
                            {referrer && (
                                <span className="inline-block px-1.5 py-0.5 rounded-sm bg-violet-600 text-white text-[9px] font-bold uppercase tracking-wider" data-testid="inv-referrer-active-badge">aktiv</span>
                            )}
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${referrerOpen ? "rotate-180" : ""}`} />
                    </button>
                    {referrerOpen && (
                      <>
                    {!referrer ? (
                        <div className="space-y-2">
                            {/* Phase 4g.P (Feb 2026, User-Wunsch): Vermittler-Suche + Transaktions-Code nebeneinander. */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-violet-900">Vermittler suchen</label>
                                    <Input
                                        value={referrerQ}
                                        onChange={(e) => setReferrerQ(e.target.value)}
                                        placeholder="Vermittler suchen (mind. 2 Zeichen: Name oder E-Mail)…"
                                        className="rounded-sm mt-1 bg-white"
                                        data-testid="inv-referrer-search"
                                    />
                                </div>
                                {/* Phase 4g.O (Feb 2026, User-Wunsch) — Transaktions-Code-Feld
                                    bereits VOR der Vermittler-Auswahl sichtbar machen, damit der
                                    Verkäufer den Code direkt eingeben kann (oft vom Vermittler
                                    telefonisch durchgegeben), ohne erst das Profil suchen zu müssen. */}
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-violet-900">Transaktions-Code (optional)</label>
                                    <Input
                                        value={referrerTxCode}
                                        onChange={(e) => setReferrerTxCode(e.target.value)}
                                        placeholder="z.B. TX-2026-001"
                                        className="rounded-sm mt-1 bg-white font-mono-num"
                                        data-testid="inv-referrer-tx-code-preselect"
                                    />
                                </div>
                            </div>
                            {referrerResults.length > 0 && (
                                <ul className="bg-white border border-gray-200 rounded-sm divide-y max-h-48 overflow-y-auto" data-testid="inv-referrer-results">
                                    {referrerResults.map((it) => (
                                        <li key={it.id}>
                                            <button
                                                type="button"
                                                onClick={() => { setReferrer(it); setReferrerResults([]); setReferrerQ(""); }}
                                                className="w-full text-left px-3 py-2 text-xs hover:bg-violet-50 flex items-center justify-between"
                                                data-testid={`inv-referrer-pick-${it.id}`}
                                            >
                                                <span>
                                                    <span className="font-bold">{it.label}</span>
                                                    <span className="text-gray-500 ml-1">· {it.email}</span>
                                                </span>
                                                <Badge variant="outline" className="text-[9px] uppercase">{it.role}</Badge>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="bg-white border border-violet-300 rounded-sm p-2 flex items-center justify-between">
                                <div className="text-xs">
                                    <span className="font-bold">{referrer.label}</span>
                                    <span className="text-gray-500 ml-1">· {referrer.email}</span>
                                </div>
                                <button type="button" onClick={() => { setReferrer(null); setReferrerCommission(""); setReferrerNote(""); }} className="text-[11px] text-red-700 hover:underline" data-testid="inv-referrer-remove">
                                    Entfernen
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Provision € *</label>
                                    <Input
                                        type="number" min="0" max="99999" step="1"
                                        value={referrerCommission}
                                        onChange={(e) => setReferrerCommission(e.target.value)}
                                        placeholder="z. B. 250"
                                        className="rounded-sm mt-1 bg-white"
                                        data-testid="inv-referrer-amount"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Transaktions-Code</label>
                                    <Input
                                        value={referrerTxCode}
                                        onChange={(e) => setReferrerTxCode(e.target.value)}
                                        placeholder="z.B. TX-2026-001"
                                        className="rounded-sm mt-1 bg-white font-mono-num"
                                        data-testid="inv-referrer-tx-code"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Notiz</label>
                                    <Input
                                        value={referrerNote}
                                        onChange={(e) => setReferrerNote(e.target.value)}
                                        placeholder="z. B. Tipp BMW R1250GS"
                                        className="rounded-sm mt-1 bg-white"
                                        data-testid="inv-referrer-note"
                                    />
                                </div>
                            </div>
                            <p className="text-[10px] text-violet-800">Bei Finalisierung wird automatisch eine Provisions-Gutschrift angelegt, die der Vermittler in seinem Profil sieht und der Admin auszahlt.</p>
                        </div>
                    )}
                      </>
                    )}
                </div>

                {/* Jul-2026 (User-Wunsch): Zahlungsbedingungen UNTER der Vermittler-Provision,
                    dunkelblaue Box mit WEISSER Schrift (Lesbarkeit). */}
                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Zahlungsbedingungen</label>
                    {isDirectSale ? (
                        <div className="mt-1 flex items-center gap-2 flex-wrap bg-blue-900 border border-blue-400 rounded-sm px-3 py-2" data-testid="inv-payment-ec-locked">
                            <span className="text-sm font-bold text-white">EC-Karte</span>
                            <span className="text-[10px] text-blue-100">Direktverkauf ist nur mit EC-Kartenzahlung möglich.</span>
                            {terminalReady ? (
                                <>
                                    <Button type="button" onClick={sendToEcTerminal} disabled={ecStatus === "sending"}
                                        className="ml-auto bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm font-bold h-8 text-xs" data-testid="inv-ec-terminal-btn">
                                        {ecStatus === "sending" ? "Sende an Terminal…" : `${fmtEur(calcTotals()?.gross || 0)} an EC-Gerät senden`}
                                    </Button>
                                    {ecStatus === "sent" && <span className="text-xs font-bold text-emerald-300" data-testid="inv-ec-terminal-sent">✓ Am Terminal bestätigen lassen</span>}
                                    {ecStatus === "error" && <span className="text-xs font-bold text-red-300">Terminal nicht erreichbar</span>}
                                </>
                            ) : null}
                        </div>
                    ) : (
                    <div className="mt-1 flex items-center gap-2 flex-wrap bg-blue-900 border border-blue-400 rounded-sm px-3 py-2" data-testid="inv-payment-row">
                        <Select value={paymentCode || "_freetext"} onValueChange={(v) => setPaymentCode(v === "_freetext" ? "" : v)}>
                            <SelectTrigger className="rounded-sm w-56 bg-blue-800 border-blue-500 h-8 text-sm font-bold text-white" data-testid="inv-payment-code"><SelectValue placeholder="Zahlungsart wählen…" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="_freetext">— Freitext —</SelectItem>
                                <SelectItem value="ec">EC-Karte</SelectItem>
                                <SelectItem value="sepa">SEPA-Einzug</SelectItem>
                                <SelectItem value="finanzierung_hausbank">Finanzierung Hausbank</SelectItem>
                                <SelectItem value="finanzierung_santander">Finanzierung Santander</SelectItem>
                                <SelectItem value="leasing_santander">Leasing Santander</SelectItem>
                                <SelectItem value="barzahlung">Barzahlung</SelectItem>
                                <SelectItem value="ueberweisung">Überweisung</SelectItem>
                            </SelectContent>
                        </Select>
                        {paymentCode === "ec" && terminalReady && (
                            <>
                                <Button type="button" onClick={sendToEcTerminal} disabled={ecStatus === "sending"}
                                    className="ml-auto bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm font-bold h-8 text-xs" data-testid="inv-ec-terminal-btn">
                                    {ecStatus === "sending" ? "Sende an Terminal…" : `${fmtEur(calcTotals()?.gross || 0)} an EC-Gerät senden`}
                                </Button>
                                {ecStatus === "sent" && <span className="text-xs font-bold text-emerald-300" data-testid="inv-ec-terminal-sent">✓ Am Terminal bestätigen lassen</span>}
                                {ecStatus === "error" && <span className="text-xs font-bold text-red-300">Terminal nicht erreichbar</span>}
                            </>
                        )}
                    </div>
                    )}
                    {paymentCode === "barzahlung" && calcTotals()?.gross >= 3000 && (
                        <p className="mt-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="inv-gwg-hint">
                            {calcTotals().gross >= 10000
                                ? "GwG-Hinweis: Barzahlungen ab 10.000 € sind ab 10.07.2027 unzulässig. Kundenidentifizierung (Ausweis) erforderlich."
                                : "GwG-Hinweis: Bei Barzahlung ab 3.000 € müssen die Ausweisdaten des Kunden erfasst werden (Geldwäschegesetz)."}
                        </p>
                    )}
                </div>

                {/* Hinweis-Box „Im nächsten Schritt…" komplett entfernt (Jul-2026, User-Wunsch). */}

                {/* Jul-2026 (User-Wunsch): Gutschein-Einlösung links neben „Weiter zur Vorschau". */}
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-[260px]" data-testid="inv-voucher">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-pink-700 mr-1">🎁 Gutschein:</span>
                        {voucherApplied ? (
                            <span className="text-[11px] font-bold px-2 py-1 rounded-full border border-red-300 bg-red-50 text-red-700 inline-flex items-center gap-1" data-testid="inv-voucher-applied">
                                {voucherApplied.code} · −{voucherApplied.label} (−{Number(voucherApplied.net).toFixed(2)} €)
                            </span>
                        ) : (
                            <>
                                {custVouchers.map((v) => (
                                    <button key={v.code} type="button" onClick={() => applyVoucher(v.code)} disabled={voucherBusy}
                                        className="text-[11px] font-bold px-2 py-1 rounded-full border border-pink-300 bg-pink-50 text-pink-800 hover:bg-pink-100 disabled:opacity-50"
                                        title={`Gültig bis ${fmtDate(v.valid_until)}`} data-testid={`inv-voucher-cust-${v.code}`}>
                                        {v.code} · −{v.discount_type === "eur" ? fmtEur(v.discount_value) : `${v.discount_value || v.discount_pct} %`}
                                    </button>
                                ))}
                                <input
                                    value={voucherCode}
                                    onChange={(e) => setVoucherCode(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyVoucher(); } }}
                                    placeholder="Code eingeben — wird automatisch geprüft"
                                    className="text-[11px] font-mono uppercase w-56 px-2 py-1 rounded-full border border-gray-300 focus:outline-none focus:border-pink-400"
                                    data-testid="inv-voucher-input"
                                />
                                {voucherBusy && <span className="text-[11px] text-gray-500" data-testid="inv-voucher-checking">prüfe…</span>}
                            </>
                        )}
                    </div>
                    <Button type="submit" disabled={busy} className="shrink-0 px-8 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="inv-submit">
                        {busy ? "Speichere…" : "Weiter zur Vorschau"}
                    </Button>
                </div>
                {voucherRestHint != null && (voucherRestHint.type === "new_voucher" ? (
                    <p className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5" data-testid="inv-voucher-rest-hint">
                        ♻️ Restguthaben von {fmtEur(voucherRestHint.amount)} wurde als <b>neuer Gutschein {voucherRestHint.code}</b> für den Kunden angelegt (gültig bis {voucherRestHint.valid_until ? new Date(voucherRestHint.valid_until).toLocaleDateString("de-DE") : "—"}).
                    </p>
                ) : (
                    <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="inv-voucher-rest-hint">
                        Hinweis: Der Gutschein hatte ein höheres Guthaben als der Rechnungsbetrag — Restguthaben von {fmtEur(voucherRestHint.amount)} verfällt und wird nicht ausgezahlt (Gutscheine sind einmalig einlösbar). Tipp: In den Gutschein-Einstellungen kann das Restguthaben automatisch als neuer Gutschein ausgegeben werden.
                    </p>
                ))}
            </form>
            )}
            {previewMode && !directDone && (
                <div className="space-y-4" data-testid="inv-preview">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-sm text-xs text-blue-900">
                        Bitte prüfe die Rechnung. Mit <strong>„Rechnung erstellen"</strong> wird sie sofort finalisiert (lückenlose Nummer, unveränderbar). Zum Ändern auf <strong>„Zurück zur Korrektur"</strong>.
                    </div>
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Kunde</div>
                        {isDirectSale ? (
                            <div className="text-sm font-medium" data-testid="inv-preview-customer">Direktverkauf (Laufkundschaft)</div>
                        ) : selectedCustomer ? (
                            <div className="text-sm border border-gray-200 rounded-sm p-3 bg-gray-50 leading-relaxed" data-testid="inv-preview-customer">
                                <div className="font-bold text-gray-900">
                                    {selectedCustomer.type === "business" && selectedCustomer.company_name ? selectedCustomer.company_name
                                        : `${selectedCustomer.first_name || ""} ${selectedCustomer.last_name || ""}`.trim() || selectedCustomer.company_name || "—"}
                                </div>
                                {selectedCustomer.type === "business" && selectedCustomer.company_name && (`${selectedCustomer.first_name || ""} ${selectedCustomer.last_name || ""}`.trim()) ? (
                                    <div className="text-gray-700">z. Hd. {`${selectedCustomer.first_name || ""} ${selectedCustomer.last_name || ""}`.trim()}</div>
                                ) : null}
                                {(selectedCustomer.street) ? <div className="text-gray-700">{selectedCustomer.street}</div> : null}
                                {(selectedCustomer.zip || selectedCustomer.city) ? <div className="text-gray-700">{[selectedCustomer.zip, selectedCustomer.city].filter(Boolean).join(" ")}{selectedCustomer.country && selectedCustomer.country !== "DE" ? ` · ${selectedCustomer.country}` : ""}</div> : null}
                                <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-gray-600">
                                    {selectedCustomer.customer_number ? <span>Kd.-Nr. {selectedCustomer.customer_number}</span> : null}
                                    {selectedCustomer.email ? <span data-testid="inv-preview-customer-email">✉ {selectedCustomer.email}</span> : null}
                                    {selectedCustomer.phone ? <span>☎ {selectedCustomer.phone}</span> : null}
                                    {selectedCustomer.ust_id ? <span>USt-ID: {selectedCustomer.ust_id}</span> : null}
                                    {selectedCustomer.tax_number ? <span>St.-Nr.: {selectedCustomer.tax_number}</span> : null}
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm font-medium text-amber-700" data-testid="inv-preview-customer">Kein Kunde ausgewählt</div>
                        )}
                    </div>
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Positionen</div>
                        <div className="border border-gray-200 rounded-sm overflow-hidden">
                            <table className="w-full text-sm">
                                <thead><tr className="text-[10px] uppercase text-gray-400 bg-gray-50"><th className="text-left px-3 py-1.5">Beschreibung</th><th className="text-right px-2">Menge</th><th className="text-right px-2">Einzel netto</th><th className="text-right px-2">MwSt</th><th className="text-right px-3">Gesamt</th></tr></thead>
                                <tbody>
                                    {items.map((it, i) => {
                                        const qty = Number(it.quantity) || 0, unit = Number(it.unit_price_net) || 0, disc = Number(it.discount_percent) || 0, vr = Number(it.vat_rate) || 0;
                                        const lineNet = it.is_differenzbesteuerung ? qty * unit : qty * unit * (1 - disc / 100);
                                        const lineGross = it.is_differenzbesteuerung ? qty * unit : lineNet * (1 + vr / 100);
                                        return (
                                            <tr key={i} className="border-t border-gray-100" data-testid={`inv-preview-item-${i}`}>
                                                <td className="px-3 py-1.5">{it.description}{it.is_differenzbesteuerung ? " · §25a" : ""}{disc > 0 ? <span className="text-red-600 font-bold" data-testid={`inv-preview-discount-${i}`}> · Rabatt −{disc}% (−{fmtEur(qty * unit * disc / 100)})</span> : null}</td>
                                                <td className="text-right px-2">{qty} {it.unit}</td>
                                                <td className="text-right px-2">{fmtEur(unit)}</td>
                                                <td className="text-right px-2">{it.is_differenzbesteuerung ? "—" : `${vr}%`}</td>
                                                <td className="text-right px-3 font-bold">{fmtEur(lineGross)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <div className="w-60 text-sm space-y-1">
                            {(() => { const t = calcTotals();
                                const discTotal = items.reduce((s, it) => s + (it.is_differenzbesteuerung ? 0 : (Number(it.quantity)||0)*(Number(it.unit_price_net)||0)*((Number(it.discount_percent)||0)/100)), 0);
                                const baseNet = t.net + discTotal;
                                const discPct = baseNet > 0 ? (discTotal / baseNet * 100) : 0;
                                return (<>
                                <div className="flex justify-between text-gray-600"><span>Netto</span><span>{fmtEur(t.net)}</span></div>
                                {discTotal > 0 && (
                                    <div className="flex justify-between text-red-600 font-bold" data-testid="inv-preview-discount-total"><span>enthaltener Rabatt (−{discPct.toFixed(discPct % 1 === 0 ? 0 : 1)}%)</span><span>− {fmtEur(discTotal)}</span></div>
                                )}
                                <div className="flex justify-between text-gray-600"><span>MwSt</span><span>{fmtEur(t.vat)}</span></div>
                                <div className="flex justify-between font-black text-base border-t border-gray-300 pt-1"><span>Gesamt</span><span data-testid="inv-preview-total">{fmtEur(t.gross)}</span></div>
                            </>); })()}
                        </div>
                    </div>
                    <div className="text-xs text-gray-500" data-testid="inv-preview-payment">Zahlungsart: {({ ec: "EC-Karte", sepa: "SEPA-Einzug", finanzierung_hausbank: "Finanzierung Hausbank", finanzierung_santander: "Finanzierung Santander", leasing_santander: "Leasing Santander", barzahlung: "Barzahlung", ueberweisung: "Überweisung" }[paymentCode]) || "Freitext"}</div>
                    {referrer && (
                        <div className="text-xs text-violet-800">Vermittler-Provision: {`${referrer.first_name || ""} ${referrer.last_name || ""}`.trim() || referrer.company_name}{referrerCommission ? ` · ${fmtEur(referrerCommission)}` : ""}</div>
                    )}
                    <div className="flex gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={() => setPreviewMode(false)} className="flex-1 rounded-sm font-bold uppercase tracking-wider" data-testid="inv-preview-back">Zurück zur Korrektur</Button>
                        <Button type="button" disabled={busy} onClick={createInvoice} className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="inv-preview-create">{busy ? "Erstelle…" : "Rechnung erstellen"}</Button>
                    </div>
                    <LegalFooter dealer={dealerProfile} />
                </div>
            )}
            {newArticle && (
                <ArticleModal
                    article={newArticle}
                    suppliers={[]}
                    onClose={() => setNewArticle(null)}
                    onSaved={(saved) => { setNewArticle(null); if (saved) addArticlePosition(saved); }}
                />
            )}
        </ModalShell>
    );
}
